import { useMemo } from 'react';
import { useGetFournisseurQuery } from 'actions/fournisseurs/queries';
import {
  useDeleteDepenseMutation,
  useUpdateDepenseMutation,
} from 'actions/depenses/mutations';
import { toast } from 'react-toastify';
import { useGetDepensesQuery } from 'actions/depenses/queries';
import { extractListData, formatQueryError } from 'utils/queryHelpers';

const useDepensesData = (dossier_id) => {
  const { data: depenseResponse, error: depenseError, isLoading: isLoadingDepenses, isFetching: isFetchingDepenses, refetch } =
    useGetDepensesQuery({ dossier_id });
  const { data: fournisseurResponse, error: fournisseurError, isLoading: isLoadingFournisseurs } =
    useGetFournisseurQuery({ dossier_id: dossier_id });

  const [updateDepense] = useUpdateDepenseMutation();
  const [deleteDepense] = useDeleteDepenseMutation();

  const updatedDonnees = useMemo(() => {
    const depenseData = extractListData(depenseResponse, depenseError, isLoadingDepenses);
    const fournisseurData = extractListData(fournisseurResponse, fournisseurError, isLoadingFournisseurs);

    if (!depenseData || depenseData.length === 0) return [];

    const fournisseurMap = fournisseurData.reduce((acc, fournisseur) => {
      acc[fournisseur.id] = fournisseur.nom;
      return acc;
    }, {});

    return depenseData.map((depense) => ({
      ...depense,
      fournisseurs: (typeof depense.fournisseurs === 'object' ? depense.fournisseurs?.nom : null) || fournisseurMap[depense.fournisseurs] || depense.fournisseur_nom || 'N/A',
    }));
  }, [depenseResponse, fournisseurResponse, depenseError, fournisseurError, isLoadingDepenses, isLoadingFournisseurs]);

  const queryError = depenseError ? formatQueryError(depenseError) : (fournisseurError ? formatQueryError(fournisseurError) : null);

  const handleUpdateDepense = async (depenseId, updateData) => {
    try {
      await updateDepense({ id: depenseId, ...updateData }).unwrap();
      await refetch();
    } catch (error) {
      toast.error(
        `Erreur lors de la mise à jour: ${error.data?.error || error.message}`
      );
      console.error('Request failed:', error);
    }
  };

  const handleDeleteDepense = async (depenseId) => {
    try {
      await deleteDepense(depenseId).unwrap();
      toast.success('Suppression réussie');
      await refetch();
    } catch (error) {
      toast.error(
        `Erreur lors de la suppression: ${error.data?.error || error.message}`
      );
    }
  };

  return {
    updatedDonnees,
    handleUpdateDepense,
    handleDeleteDepense,
    refetch,
    error: queryError,
    isLoading: isLoadingDepenses || (isFetchingDepenses && (!depenseResponse || extractListData(depenseResponse).length === 0)),
  };
};

export default useDepensesData;

